import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";
import { Seo } from "@/components/Seo";
import { SEGMENT_SEO, serviceLd, breadcrumbLd } from "@/lib/seo";
import { ArrowRight } from "lucide-react";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

// Landing pages reachable from the homepage segment cards.
const SEGMENTS = {
  founders: {
    label: "For founders",
    title: "For founders.",
    blurb:
      "You are building something real. Most founders start with a Sprint and climb the ladder only when the roadmap calls for it. We bring the senior technical judgment to get the architecture, the build, and the funding story right.",
    showRefer: false,
  },
  entrepreneurs: {
    label: "For entrepreneurs",
    title: "For entrepreneurs.",
    blurb:
      "You have the idea. Make it real on paper before you spend a dollar building. We define the customer, pressure-test the model, and hand you a roadmap, so the build, when it comes, is the right one.",
    showRefer: false,
  },
  enterprises: {
    label: "For enterprises",
    title: "For enterprises.",
    blurb:
      "Your AI ambition is running ahead of the governance and infrastructure to support it. We carry the documentation, decision rights, and compliance that hold up under board and regulatory scrutiny. Independent by design, with no platform to sell you.",
    showRefer: false,
  },
  nonprofits: {
    label: "For nonprofits and institutions",
    title: "For nonprofits and institutions.",
    blurb:
      "You run a mission-driven organization. We carry the governance, continuity, and funder-ready reporting your board and your funders expect, without adding a full-time executive to the budget. We build to the workflows you already run, never the reverse.",
    showRefer: false,
  },
  advisors: {
    label: "For advisors",
    title: "For advisors.",
    blurb:
      "You work alongside founders and operators who keep raising technology questions you are not positioned to answer. Refer them to Copper & Vine and earn a rate that respects your network.",
    showRefer: true,
  },
};

export default function ForSegment() {
  const { segment } = useParams();
  const { openBrief } = useBrief();
  const seg = SEGMENTS[segment];

  if (!seg) return <Navigate to="/services" replace />;

  const meta = SEGMENT_SEO[segment];

  return (
    <div className="exec-scope min-h-screen" data-testid={`for-${segment}-page`}>
      <Seo
        title={meta.title}
        description={meta.description}
        path={`/for/${segment}`}
        jsonLd={[
          serviceLd({ name: meta.title, description: meta.description, path: `/for/${segment}` }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: seg.label, path: `/for/${segment}` },
          ]),
        ]}
      />
      <section className="exec-container pt-24 md:pt-28 pb-12">
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow" data-testid="for-segment-kicker">{seg.label}</p>
          <h1 className="font-extralight text-[clamp(36px,5vw,64px)] leading-[1.08] mt-6">
            {seg.title}
          </h1>
          <p data-testid="for-segment-blurb" className="exec-serif text-[clamp(18px,2.1vw,24px)] leading-[1.5] text-[color:var(--lead-ink)] mt-7">
            {seg.blurb}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link
              to="/scorecard"
              data-testid="for-segment-scorecard-cta"
              onClick={() => track("door_click", { target: "/scorecard", location: `for_${segment}` })}
              className="exec-btn exec-btn-primary"
            >
              Take the Scorecard
            </Link>
            {seg.showRefer ? (
              <Link
                to="/refer"
                data-testid="for-segment-refer-cta"
                onClick={() => track("door_click", { target: "/refer", location: `for_${segment}` })}
                className="exec-btn exec-btn-secondary"
              >
                Refer a founder <ArrowRight size={14} />
              </Link>
            ) : (
              <button
                data-testid="for-segment-brief-cta"
                className="exec-btn exec-btn-secondary"
                onClick={() => {
                  track("door_click", { target: "brief_request", location: `for_${segment}` });
                  openBrief("general");
                }}
              >
                Request the brief
              </button>
            )}
          </div>
        </motion.div>
      </section>

      {/* Light internal linking + scaffold note */}
      <section className="exec-container pb-24">
        <div className="max-w-[820px] border-t border-[color:var(--rule)] pt-8">
          <p className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)]">
            Tailored guidance for this path is in progress. In the meantime, the Readiness Scorecard
            is the fastest way to see where you stand, and the ladder shows exactly how an engagement
            is structured.
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
            <Link to="/services" data-testid="for-segment-ladder-link" className="exec-link">
              See the ladder <ArrowRight size={14} />
            </Link>
            <Link to="/work" data-testid="for-segment-work-link" className="exec-link">
              See the work <ArrowRight size={14} />
            </Link>
            <Link to="/resources" data-testid="for-segment-resources-link" className="exec-link">
              Frameworks and field notes <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
